import { usePermissions } from "@/modules/auth/presentation/model/usePermissions";
import type { IngredientListRow } from "@/modules/inventory/domain/types";
import { RowActions } from "@/shared/ui/RowActions";
import { confirm } from "@/shared/lib/confirm";
import { DataTable, createSortableColumn, TanstackReactTable } from "@/shared/ui/data-table";
import { Badge } from "@/components/ui/badge";



import { Checkbox } from "@/components/ui/checkbox";
import { EditIcon, TrashIcon } from "lucide-react";

type IngredientsTableProps = {
  data: IngredientListRow[];
  isLoading?: boolean;
  offset: number;
  limit: number;
  totalCount: number;
  onPaginationChange: (offset: number, limit: number) => void;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onSortingChange: (id: string, desc: boolean) => void;
  onEdit: (row: IngredientListRow) => void;
  onDelete: (id: string) => void;
  selectable?: boolean;
  selectedIds?: Set<string>;
  onSelectedIdsChange?: (ids: Set<string>) => void;
};

export function IngredientsTable({
  data,
  isLoading,
  offset,
  limit,
  totalCount,
  onPaginationChange,
  sortBy,
  sortOrder,
  onSortingChange,
  onEdit,
  onDelete,
  selectable,
  selectedIds,
  onSelectedIdsChange,
}: IngredientsTableProps) {
  const { has } = usePermissions();

  const pageIds = data.map((r) => r.id);
  const allPageSelected =
    selectable &&
    pageIds.length > 0 &&
    pageIds.every((id) => selectedIds?.has(id));

  const toggleAllPage = () => {
    if (!onSelectedIdsChange || !selectedIds) return;
    const next = new Set(selectedIds);
    if (allPageSelected) {
      for (const id of pageIds) next.delete(id);
    } else {
      for (const id of pageIds) next.add(id);
    }
    onSelectedIdsChange(next);
  };

  const toggleOne = (id: string, checked: boolean) => {
    if (!onSelectedIdsChange || !selectedIds) return;
    const next = new Set(selectedIds);
    if (checked) next.add(id);
    else next.delete(id);
    onSelectedIdsChange(next);
  };

  const columns: TanstackReactTable.ColumnDef<IngredientListRow>[] = [
    ...(selectable
      ? [
          {
            id: "select",
            header: () => (
              <Checkbox
                checked={allPageSelected}
                onCheckedChange={() => toggleAllPage()}
                aria-label="ເລືອກທັງໜ້າ"
              />
            ),
            cell: ({ row }: { row: { original: IngredientListRow } }) => (
              <Checkbox
                checked={selectedIds?.has(row.original.id) ?? false}
                onCheckedChange={(v) =>
                  toggleOne(row.original.id, v === true)
                }
                aria-label="ເລືອກ"
              />
            ),
            size: 40,
            enableSorting: false,
          } as TanstackReactTable.ColumnDef<IngredientListRow>,
        ]
      : []),
    createSortableColumn<IngredientListRow>("name", "ຊື່", { size: 180 }),
    createSortableColumn<IngredientListRow>("baseUnit", "ຫນ່ວຍພື້ນຖານ", {
      size: 110,
    }),
    {
      id: "receiveUnit",
      header: "ຫນ່ວຍຮັບເຂົ້າ",
      cell: ({ row }) =>
        `${row.original.receiveUnit} ×${row.original.receiveToBase}`,
      size: 130,
    },
    {
      id: "issueUnit",
      header: "ຫນ່ວຍຕັດຂາຍ",
      cell: ({ row }) =>
        `${row.original.issueUnit} ×${row.original.issueToBase}`,
      size: 130,
    },
    {
      id: "categoryName",
      header: "ຫມວດ",
      cell: ({ row }) =>
        row.original.categoryName ?? (
          <span className="text-muted-foreground">—</span>
        ),
      size: 140,
    },
    {
      id: "isActive",
      header: "ສະຖານະ",
      cell: ({ row }) =>
        row.original.isActive ? (
          <Badge variant="default">ເປີດ</Badge>
        ) : (
          <Badge variant="secondary">ປິດ</Badge>
        ),
      size: 80,
    },
    {
      size: 120,
      id: "actions",
      cell: ({ row }: { row: { original: IngredientListRow } }) => {
        const id = row.original.id;
        const actions: {
          label: string;
          icon?: React.ReactNode;
          variant?: "destructive";
          onClick: () => void;
        }[] = [];

        if (has("inventory:update")) {
          actions.push({
            label: "ແກ້ໄຂ",
            icon: <EditIcon className="h-4 w-4" />,
            onClick: () => onEdit(row.original),
          });
        }
        if (has("inventory:delete")) {
          actions.push({
            label: "ລຶບ",
            variant: "destructive",
            icon: <TrashIcon className="h-4 w-4" />,
            onClick: async () => {
              const ok = await confirm({
                title: "ລຶບວັດຖຸດິບ",
                description:
                  "ທ່ານແນ່ໃຈບໍ່ວ່າຈະລຶບວັດຖຸດິບນີ້?",
                actionText: "ລຶບ",
                ActionProps: { variant: "destructive" },
              });
              if (ok) onDelete(id);
            },
          });
        }

        return <RowActions actions={actions} maxInline={3} />;
      },
    },
  ];

  return (
    <DataTable<IngredientListRow, unknown>
      noDataMessage="ບໍ່ພົບວັດຖຸດິບ"
      isLoading={isLoading}
      columns={columns}
      data={data}
      offset={offset}
      limit={limit}
      totalCount={totalCount}
      onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
      sortBy={sortBy}
      sortOrder={sortOrder}
      onSortingChange={(sorting) => {
        if (sorting[0]?.id === "") return;
        onSortingChange(sorting[0]?.id as string, !!sorting[0]?.desc);
      }}
    />
  );
}
